
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { ensureValidToken, handleAuthError } from "@/services/AuthAwareApiService";

interface SchemaResponse {
  sql: string;
  executionSuccess: boolean;
  message?: string;
  tableName: string;
  columnsCreated?: number;
  isFallback?: boolean;
  emergencyMode?: boolean;
  error?: string;
  details?: string;
}

interface ImportResponse {
  message: string;
  recordCount: number;
  mappingStats?: {
    exact: number;
    similarity: number;
    unmapped: number;
  };
}

export const useCsvTableOperations = (refetch: () => Promise<any>) => {
  const [processingId, setProcessingId] = useState<string | null>(null);

  const handleGenerateSchema = async (analysisId: string, tableName: string): Promise<boolean> => {
    if (!tableName?.trim()) {
      toast({
        title: "Error",
        description: "Please enter a table name",
        variant: "destructive",
      });
      return false;
    }

    const sanitizedTableName = tableName.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    
    setProcessingId(analysisId);
    try {
      console.log('[FRONTEND] Starting schema generation for table:', sanitizedTableName);

      // Ensure valid token before making API call
      await ensureValidToken('generate-table-schema');

      const { data: response, error: schemaError } = await supabase.functions.invoke<SchemaResponse>("generate-table-schema", {
        body: { 
          csvAnalysisId: analysisId,
          tableName: sanitizedTableName
        },
      });

      if (schemaError) {
        console.error('[FRONTEND] Edge function error:', schemaError);
        
        // Show more detailed error message
        toast({
          title: "Schema Generation Failed",
          description: `Network error: ${schemaError.message}. Check the function logs for details.`,
          variant: "destructive",
        });
        return false;
      }

      if (!response) {
        toast({
          title: "Schema Generation Failed",
          description: "No response from schema generation service. The function may have timed out or crashed.",
          variant: "destructive",
        });
        return false;
      }

      // Check if the response indicates an error
      if (response.error || !response.executionSuccess) {
        console.error('[FRONTEND] Schema generation failed:', response);
        
        let errorMessage = response.error || response.message || "Unknown error occurred";
        if (response.details) {
          console.error('[FRONTEND] Error details:', response.details);
          errorMessage += ` (Check console for details)`;
        }
        
        toast({
          title: "Schema Generation Failed",
          description: errorMessage,
          variant: "destructive",
        });
        return false;
      }

      console.log('[FRONTEND] Schema generation successful:', response);

      // Show success message with additional info for fallback schemas
      let successMessage = response.message || `Table ${sanitizedTableName} created successfully`;
      
      if (response.isFallback || response.emergencyMode) {
        successMessage += ". Note: Used generic column structure due to file size. You may need to adjust column types after import.";
      }

      toast({
        title: "Success",
        description: successMessage,
        className: "bg-[#F2FCE2] border-green-200 text-green-800",
      });

      refetch();
      return true;
    } catch (error: any) {
      console.error('[FRONTEND] Schema generation error:', error);
      toast({
        title: "Schema Generation Failed",
        description: error.message || "Failed to generate schema and create table. Check console and function logs for details.",
        variant: "destructive",
      });
      return false;
    } finally {
      setProcessingId(null);
    }
  };

  const handleImportData = async (analysisId: string, tableName: string, filePath: string) => {
    if (!tableName?.trim()) {
      toast({
        title: "Error",
        description: "Please enter a table name",
        variant: "destructive",
      });
      return;
    }

    const sanitizedTableName = tableName.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');

    // Verify table exists before attempting import
    const { data: tableInfo, error: tableError } = await supabase
      .rpc('get_table_info', { table_name: sanitizedTableName });

    const tableInfoArray = Array.isArray((tableInfo as any)?.table_info)
      ? (tableInfo as any).table_info
      : Array.isArray(tableInfo)
        ? (tableInfo as any)
        : [];
    if (tableError || tableInfoArray.length === 0) {
      console.warn('Table not found or could not fetch schema info');
      toast({
        title: "Table Not Found",
        description: `Table "${sanitizedTableName}" does not exist. Please generate the schema first.`,
        variant: "destructive",
      });
      return;
    }

    setProcessingId(analysisId);
    
    // Show initial progress toast
    toast({
      title: "Import Starting",
      description: "Processing CSV data and mapping columns...",
      className: "bg-blue-50 border-blue-200 text-blue-800",
    });

    try {
      console.log('[FRONTEND] Starting import for table:', sanitizedTableName);
      
      // Ensure valid token before making API call
      await ensureValidToken('import-csv-data');
      
      const { data: importResult, error: importError } = await supabase.functions.invoke<ImportResponse>("import-csv-data", {
        body: { 
          csvAnalysisId: analysisId,
          tableName: sanitizedTableName
        },
      });

      if (importError) {
        console.error('[FRONTEND] Import error:', importError);
        
        // Try to get error analysis
        const { data: fileData } = await supabase.storage
          .from('csv_uploads')
          .download(filePath);
        
        const csvText = await fileData?.text();
        
        // Ensure valid token for error analysis
        await ensureValidToken('analyze-csv-error');
        
        const { data: analysis, error: analysisError } = await supabase.functions.invoke("analyze-csv-error", {
          body: {
            error: importError,
            csvData: csvText,
            tableName: sanitizedTableName
          }
        });

        if (analysisError) throw analysisError;

        toast({
          title: "Import Failed - AI Analysis",
          description: analysis.analysis,
          variant: "destructive",
        });
        return;
      }

      if (importResult) {
        const { recordCount, mappingStats } = importResult;
        
        let mappingDetails = "";
        if (mappingStats) {
          const totalMapped = mappingStats.exact + mappingStats.similarity;
          mappingDetails = ` Using ${mappingStats.exact} exact mappings and ${mappingStats.similarity} similarity-based mappings.`;
          
          if (mappingStats.unmapped > 0) {
            mappingDetails += ` ${mappingStats.unmapped} columns could not be mapped.`;
          }
        }

        toast({
          title: "Import Successful!",
          description: `Successfully imported ${recordCount.toLocaleString()} records into table "${sanitizedTableName}".${mappingDetails}`,
          className: "bg-[#F2FCE2] border-green-200 text-green-800",
        });
      } else {
        toast({
          title: "Import Completed",
          description: `Data imported successfully into table ${sanitizedTableName}`,
          className: "bg-[#F2FCE2] border-green-200 text-green-800",
        });
      }

      refetch();
    } catch (error: any) {
      console.error('[FRONTEND] Import error:', error);
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import data. Please check your CSV format and table structure.",
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  return {
    processingId,
    handleGenerateSchema,
    handleImportData,
  };
};
